if (!customElements.get('shop-feed')) {
    class ShopFeed extends HTMLElement {
        constructor() {
            super();
            this.currentVideoPlayer = this._getAutoPlayVideoElement() || null;

            this.addEventListener(
                'video_playing',
                this.handleVideoPlaying.bind(this)
            );
            this.addEventListener('click', (event) => {
                const trigger = event.target.closest('[data-shoppable-video-url]');
                if (!trigger) return;
                this.checkAndPauseAnyVideoPlaying();
                this.querySelector('shoppable-feed-video-modal')?.open(trigger);
            });
            if (this.currentVideoPlayer && this.hasAttribute('play-first-media')) {
                this.addEventListener(_EVENT_HELPER.elementIsInCurrentView, (e) => {
                    this.currentVideoPlayer._renderMedia();
                });
            }

            HELPER_UTIL.trigerElementFunctionalityInView(
                this,
                null,
                this.checkInView.bind(this)
            );

            // Pause feed video if options drawer opens
            this._boundShowDrawer = (e) => {
                if (e.detail.drawerType !== 'options') return;
                this.checkAndPauseAnyVideoPlaying();
            };
            document.addEventListener(_EVENT_HELPER.showDrawer, this._boundShowDrawer);

            this.addEventListener('shoppable-feed-slide-change', (e) => {
                !e.detail.activeSlide.querySelector('feed-player, reel-feed-player') && this.checkAndPauseAnyVideoPlaying();

            });
        }

        handleVideoPlaying(e) {
            const newVideoPlayer = e.target;
            if (!this.currentVideoPlayer) {
                this.currentVideoPlayer = newVideoPlayer;
            }
            if (this.currentVideoPlayer && this.currentVideoPlayer != newVideoPlayer) {
                this.currentVideoPlayer._pause();
                this.currentVideoPlayer = newVideoPlayer;

            }
        }

        checkAndPauseAnyVideoPlaying() {
            if (!this.currentVideoPlayer) return;
            this.currentVideoPlayer._pause();
        }

        disconnectedCallback() {
            document.removeEventListener(_EVENT_HELPER.showDrawer, this._boundShowDrawer);
        }

        checkInView(e) {
            this.checkAndPauseAnyVideoPlaying();
        }

        _getAutoPlayVideoElement() {
            return [...this.querySelectorAll('feed-player')].find((el) =>
                el.hasAttribute('feed-media-autoplay')
            );
        }


    }

    customElements.define('shop-feed', ShopFeed);
}

if (!customElements.get('shoppable-feed-video-modal')) {
    class ShoppableFeedVideoModal extends HTMLElement {
        connectedCallback() {
            this.dialog = this.querySelector('dialog');
            this.content = this.querySelector('[data-shoppable-video-content]');
            this.error = this.querySelector('[data-shoppable-video-error]');
            this.source = this.querySelector('[data-shoppable-video-source]');
            this.closeButton = this.querySelector('[data-shoppable-video-close]');
            this.listeners?.abort();
            this.listeners = new AbortController();
            const options = { signal: this.listeners.signal };
            this.closeButton.addEventListener('click', () => this.close(), options);
            this.dialog.addEventListener('keydown', (event) => {
                if (event.key !== 'Tab') return;
                const last = this.source.hidden ? this.closeButton : this.source;
                if (event.shiftKey && document.activeElement === this.closeButton) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    this.closeButton.focus();
                }
            }, options);
            this.dialog.addEventListener('click', (event) => {
                // The native backdrop reports the dialog as its target, including clicks in its padding.
                const bounds = this.dialog.getBoundingClientRect();
                if (event.target === this.dialog && (
                    event.clientX < bounds.left || event.clientX > bounds.right ||
                    event.clientY < bounds.top || event.clientY > bounds.bottom
                )) this.close();
            }, options);
            this.dialog.addEventListener('close', () => {
                if (!this.dialog.open) this.reset();
            }, options);
            document.addEventListener(_EVENT_HELPER.showDrawer, () => this.close(), options);
        }

        disconnectedCallback() {
            this.close();
            this.listeners?.abort();
        }

        getVideo(rawUrl) {
            try {
                const url = new URL(rawUrl);
                if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
                const host = url.hostname.toLowerCase().replace(/^www\./, '');
                const parts = url.pathname.split('/').filter(Boolean);
                let id;
                if (host === 'youtu.be') {
                    id = parts[0];
                } else if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com'].includes(host)) {
                    id = parts[0] === 'watch' ? url.searchParams.get('v') :
                        ['embed', 'shorts', 'live'].includes(parts[0]) ? parts[1] : null;
                }
                if (id && /^[\w-]{11}$/.test(id)) {
                    return { provider: 'youtube', id, url: `https://www.youtube.com/watch?v=${id}` };
                }
                if (host === 'instagram.com' && ['p', 'reel', 'reels', 'tv'].includes(parts[0]) &&
                    /^[\w-]+$/.test(parts[1] || '')) {
                    const type = parts[0] === 'reels' ? 'reel' : parts[0];
                    return { provider: 'instagram', url: `https://www.instagram.com/${type}/${parts[1]}/` };
                }
            } catch (_) {
                return null;
            }
            return null;
        }

        open(trigger) {
            document.querySelectorAll('shoppable-feed-video-modal').forEach((modal) => modal.close());
            this.trigger = trigger;
            const video = this.getVideo(trigger.dataset.shoppableVideoUrl);
            this.previousOverflow = document.body.style.overflow;
            this.dialog.showModal();
            HELPER_UTIL.bodyInteractions.disableScroll();
            this.error.hidden = !!video;
            if (!video) return;

            this.dialog.dataset.provider = video.provider;
            this.source.href = video.url;
            this.source.hidden = false;
            if (video.provider === 'youtube') {
                const iframe = document.createElement('iframe');
                iframe.src = `https://www.youtube.com/embed/${video.id}?autoplay=1&playsinline=1&rel=0`;
                iframe.title = this.content.dataset.playerTitle;
                iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
                iframe.allowFullscreen = true;
                iframe.referrerPolicy = 'strict-origin-when-cross-origin';
                this.content.appendChild(iframe);
            } else {
                this.renderInstagram(video.url);
            }
        }

        async renderInstagram(url) {
            const post = document.createElement('blockquote');
            post.className = 'instagram-media';
            post.dataset.instgrmPermalink = url;
            post.dataset.instgrmVersion = '14';
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.textContent = this.source.textContent.trim();
            post.appendChild(link);
            this.content.appendChild(post);
            try {
                await ShoppableFeedVideoModal.loadInstagram();
                // A closed/replaced popup must not resurrect an embed after the script loads.
                if (this.dialog.open && this.content.contains(post)) window.instgrm.Embeds.process();
            } catch (_) {
                if (this.dialog.open && this.content.contains(post)) this.error.hidden = false;
            }
        }

        static loadInstagram() {
            if (window.instgrm?.Embeds) return Promise.resolve();
            if (!this.instagramScript) {
                this.instagramScript = new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://www.instagram.com/embed.js';
                    script.async = true;
                    script.onload = () => window.instgrm?.Embeds ? resolve() : reject(new Error('Instagram unavailable'));
                    script.onerror = () => reject(new Error('Instagram unavailable'));
                    document.head.appendChild(script);
                }).catch((error) => {
                    this.instagramScript = null;
                    throw error;
                });
            }
            return this.instagramScript;
        }

        close() {
            if (!this.dialog?.open) return;
            this.dialog.close();
            this.reset();
        }

        reset() {
            // Removing the iframe stops playback, including when Escape closes the native dialog.
            this.content.replaceChildren();
            this.source.hidden = true;
            this.source.removeAttribute('href');
            this.error.hidden = true;
            delete this.dialog.dataset.provider;
            if (this.previousOverflow !== undefined) {
                document.body.style.overflow = this.previousOverflow;
                this.previousOverflow = undefined;
            }
            if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
            this.trigger = null;
        }
    }

    customElements.define('shoppable-feed-video-modal', ShoppableFeedVideoModal);
}

if(!customElements.get('shoppable-feed-card')){
    class ShoppableFeedCard extends HTMLElement {
        constructor() {
            super();
            this.mediaPlayElement = this.querySelector('[media-interaction-element]');
        }

        connectedCallback() {}
    }

    customElements.define('shoppable-feed-card', ShoppableFeedCard);
}

if (!customElements.get('feed-player')) {
class FeedPlayer extends defaultVideoPlayer {
    constructor() {
        super();
        this.pauseVideoEl = this.querySelectorAll('[pause-media]');
        this.audioEl = this.querySelector('[audio-state]');
        this.playOnHover = this.hasAttribute("play-on-hover")
        this.pauseVideoEl &&
            this.pauseVideoEl.forEach((el) => {
                el.addEventListener('click', this.whenVideoPause.bind(this))
            }
            );
        this.audioState = this.audioEl?.getAttribute('audio-state') || 'mute';
        this.playMuteVideo = this.audioState && this.audioState === 'mute' ? true : false ;
        this.isUGCContext = !!this.closest('snap-slider');
        this.audioEl && this.audioEl.addEventListener('click', this.handleAudioState.bind(this));
        this.playOnHover && this.addEventListener("mouseenter", this.playONMouseHover.bind(this))
        this.playOnHover && this.addEventListener("mouseleave", this.pauseONMouseLeve.bind(this))

    }
    playONMouseHover() {
        if (this.getAttribute("video-playing") == 'false') {
            const audioState = this.audioEl ? this.audioEl.getAttribute('audio-state') : 'mute';
            this.player.muted = audioState !== 'unmute';
            this._renderMedia();
        }
    }
    pauseONMouseLeve() {
        this._pause()

    }
    handleAudioState() {
        const audioState = this.audioEl.getAttribute('audio-state');
        switch (audioState) {
            case 'mute':
                this.audioEl.setAttribute('audio-state', 'unmute');
                this.player.muted = false;
                if (this.isUGCContext) {
                    this.audioState = 'unmute';
                    this.playMuteVideo = false;
                }
                break;
            case 'unmute':
                this.audioEl.setAttribute('audio-state', 'mute');
                this.player.muted = true;
                if (this.isUGCContext) {
                    this.audioState = 'mute';
                    this.playMuteVideo = true;
                }
                break;
            case 'default':
                break;
        }
    }


    whenVideoPause() {
        this._pause();
    }

    _pause() {
        super._pause();
        this.setAttribute("video-playing", false);
    }

    _play() {
        this.player.muted = this.playMuteVideo;
        if (this.audioEl) {
            this.audioEl.setAttribute('audio-state', this.audioState);
        }
        HELPER_UTIL.dispatchCustomEvent('video_playing', this);
        super._play();
        this.setAttribute("video-playing", true);
    }
    showPlayButton() {
        this.playButton.classList.remove('video-elements-hidden');
    }
}

customElements.define('feed-player', FeedPlayer);

class ReelFeedPlayer extends FeedPlayer {
    constructor() {
        super();
        this.progressBar = this.querySelector('[video-progress-bar-fill]');
        this._boundVideoEnded = this._videoEnded.bind(this);
        this._rafId = null;
    }

    connectedCallback() {
        if (this.player) {
            this.player.addEventListener('ended', this._boundVideoEnded);
        }
    }

    disconnectedCallback() {
        if (this.player) {
            this.player.removeEventListener('ended', this._boundVideoEnded);
        }
        this._stopProgress();
    }

    // Use requestAnimationFrame for smooth 60fps progress bar updates
    _startProgress() {
        this._stopProgress();
        const update = () => {
            if (!this.player || !this.progressBar) return;
            const percentage = this.player.duration
                ? (this.player.currentTime / this.player.duration) * 100
                : 0;
            this.progressBar.style.width = `${percentage}%`;
            this._rafId = requestAnimationFrame(update);
        };
        this._rafId = requestAnimationFrame(update);
    }

    _stopProgress() {
        if (this._rafId !== null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
    }

    _play() {
        super._play();
        this._startProgress();
    }

    _pause() {
        super._pause();
        this._stopProgress();
    }

    _videoEnded() {
        this._stopProgress();
        HELPER_UTIL.dispatchCustomEvent('video_ended', this);
        if (this.progressBar) this.progressBar.style.width = '0';
    }

    _resetVideo() {
        if (!this.player) return;
        this._stopProgress();
        this.player.currentTime = 0;
        if (this.progressBar) this.progressBar.style.width = '0';
    }
}

customElements.define('reel-feed-player', ReelFeedPlayer);
}

if (!customElements.get('shoppable-carousel-component')) {
class ShoppableCarouselComponent extends CarouselComponent {
    constructor() {
        super();
        this.shopFeedElement = this.closest('shop-feed');
    }

    handleFunctionalityAfterSlideChange(swiperInstance) {
        super.handleFunctionalityAfterSlideChange(swiperInstance);
        if (!this.hasAttribute("centered-slides")) return;
        HELPER_UTIL.dispatchCustomEvent('shoppable-feed-slide-change', this.shopFeedElement, {
            activeSlide: swiperInstance.slides[swiperInstance.activeIndex]
        });
    }
}

customElements.define('shoppable-carousel-component', ShoppableCarouselComponent);
}




if (!customElements.get('ugc-reel')) {
class UGCReel extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.mediaItems = this.querySelectorAll('[reel-swiper-container] [data-media-type="video"], [reel-swiper-container] [data-media-type="image"]');
        this.portalElementId = this.getAttribute('target-portal-element') || null;
        this.popupDrawerType = this.getAttribute('popup-drawer-type') || 'ugc-reel-pop';
        this.snapSlider = this.querySelector('[main-snap-slider]');
        this.buttonLabel = this.getAttribute('button-label');
        this._initFirstVideoAutoplay();
        this._updateButtonLabel();
        if (Shopify.designMode) this._closeAndRemoveStaleDrawers();
        this._mediaHandlers = [];
        this.mediaItems.forEach((item, index) => {
            const clickHandler = () => this.openPopup(index);
            const keydownHandler = (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.openPopup(index);
                }
            };
            item.addEventListener('click', clickHandler);
            item.addEventListener('keydown', keydownHandler);
            this._mediaHandlers.push({ item, clickHandler, keydownHandler });
        });
    }

    disconnectedCallback() {
        this._mediaHandlers?.forEach(({ item, clickHandler, keydownHandler }) => {
            item.removeEventListener('click', clickHandler);
            item.removeEventListener('keydown', keydownHandler);
        });
        this._mediaHandlers = [];
    }

    _closeAndRemoveStaleDrawers() {
        if (!this.portalElementId) return;
        const portal = document.querySelector(this.portalElementId);
        if (!portal) return;
        portal.querySelectorAll(`drawer-component[drawer-type="${this.popupDrawerType}"]`).forEach(drawer => {
            drawer.classList.remove('drawer-active');
            drawer.isDrawerOpen = false;
            drawer.remove();
        });
        HELPER_UTIL.bodyInteractions.enableScroll();
    }

    openPopup(index) {
        HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, document, {
            drawerType: this.popupDrawerType,
            portalDrawerTo: this.portalElementId,
            portalBackSectionId: this.id,
        });
        HELPER_UTIL.dispatchCustomEvent('click-to-slide', this.snapSlider, {
            currentIndex: index
        });
    }

    _initFirstVideoAutoplay() {
        if (!this.hasAttribute('first-video-play')) return;
        const firstBlock = this.querySelector('[reel-swiper-container] [data-media-type]');
        if (!firstBlock || firstBlock.dataset.mediaType !== 'video') return;
        this.firstVideoPlayer = firstBlock;
        this._firstVideoPlayed = false;
        this.addEventListener(_EVENT_HELPER.elementIsInCurrentView, () => {
            if (this._firstVideoPlayed) return;
            this._firstVideoPlayed = true;
            this._playFirstVideoMuted();
        });
    }

    _playFirstVideoMuted() {
        if (!this.firstVideoPlayer?.player) return;
        this.firstVideoPlayer.player.muted = true;
        this.firstVideoPlayer.player.loop = true;
        this.firstVideoPlayer.playMuteVideo = true;
        this.firstVideoPlayer.querySelector('[pause-media]')?.remove();
        this.firstVideoPlayer._renderMedia();
    }

    _updateButtonLabel() {
        if (!this.buttonLabel) return;
        this.querySelectorAll('[main-snap-slider] [snap-slider-item] [quick-buy-style-button-label]')
            .forEach((el) => { el.innerText = this.buttonLabel; });
    }
}

customElements.define('ugc-reel', UGCReel);
}
